import { Button } from "@deligoo/ui"
import { unwrapResult } from "@reduxjs/toolkit"
import { useState } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { useAppDispatch, useAppSelector } from "@/App"
import { AsidePanel } from "@/components/AsidePanel"
import { Card } from "@/components/Card"
import { Clipboard } from "@/components/Clipboard"
import { Tabs } from "@/components/Tabs"
import { requestEditOrder, selectOrderById } from "@/store/orders"
import { selectCurrentTeamId } from "@/store/teams"
import { EditOrder, Order } from "@/types"

import cls from "./EditOrderPanel.module.scss"
import { Costs, Delivery, Details } from "./partials"

const TABS = ["details", "delivery", "costs"] as const

type EditOrderPanelProps = {
  orderId: Order["id"]
  handleClose: () => void
}

export const EditOrderPanel = ({ orderId, handleClose }: EditOrderPanelProps) => {
  const order = useAppSelector(selectOrderById(orderId))
  const teamId = useAppSelector(selectCurrentTeamId)

  const [currentTab, setCurrentTab] = useState<string>(TABS[0])

  const form = useForm<Partial<EditOrder>>({
    mode: "onChange",
    defaultValues: {
      payment_form: order?.payment_form,
      price_subunit: order?.price_subunit || 0,
      price_delivery_subunit: order?.price_delivery_subunit || 0,
      distance_cost_subunit: order?.distance_cost_subunit || 0,
      transport_types: order?.transport_types,
      distance_for_car: order?.distance_for_car || undefined,
      distance_for_bicycle: order?.distance_for_bicycle || undefined,
      distance_for_scooter: order?.distance_for_scooter || undefined,
      pickup_attributes: {
        id: order?.pickup?.id,
      },
      delivery_attributes: {
        id: order?.delivery?.id,
      },
    },
  })

  const { replace } = useFieldArray({
    control: form.control,
    name: "additional_fees_attributes",
    keyName: "additionalFeeId",
  })

  const dispatch = useAppDispatch()

  const { t } = useTranslation("orders")

  if (!order) return null

  async function onSubmit(data: Partial<EditOrder>) {
    if (!order || !teamId) return

    const response = await dispatch(
      requestEditOrder({
        teamId,
        orderId: order.id,
        body: data,
      })
    ).then(unwrapResult)

    if (response.ok && response.data) {
      replace(response.data?.additional_fees)

      toast.success("Zlecenie zostało zaktualizowane", {
        toastId: "editOrder",
        updateId: "editOrder",
      })
    }
  }

  return (
    <AsidePanel handleClose={handleClose} width="45rem">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <Card.Section>
              <div className="d-flex justify-between align-center mb-8">
                <div>
                  <h1 className="h400 mb-2">Edytuj zlecenie</h1>
                  <Clipboard className="h200 font-weight-bold text-primary">{order.uid}</Clipboard>
                </div>

                <div className={cls.actions}>
                  <Button onClick={handleClose} className="ml-6">
                    Anuluj
                  </Button>

                  <Button color="primary" className="ml-6" type="submit">
                    Zapisz zmiany
                  </Button>
                </div>
              </div>

              <Tabs currentTab={currentTab} setCurrentTab={setCurrentTab}>
                {TABS.map((tab) => (
                  <Tabs.Tab label={t(`edit.${tab}`)} value={tab} key={tab} />
                ))}
              </Tabs>
            </Card.Section>

            {currentTab === "details" && <Details orderId={orderId} />}
            {currentTab === "delivery" && <Delivery orderId={orderId} />}
            {currentTab === "costs" && <Costs orderId={orderId} />}
          </Card>
        </form>
      </FormProvider>
    </AsidePanel>
  )
}
